import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import { fetchPGATourTournaments } from '../adminApiService';
import './PGATourFetchModal.css';

const PGATourFetchModal = ({
  showModal,
  setShowModal,
  onConfirmCreate,
  isLoading
}) => {
  const [tournaments, setTournaments] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTournaments, setSelectedTournaments] = useState(new Set());

  useEffect(() => {
    if (showModal) {
      fetchPGATournaments();
    }
  }, [showModal]);

  const fetchPGATournaments = async () => {
    setIsFetching(true);
    setError(null);
    
    try {
      // Use the adminApiService function
      const tournamentsData = await fetchPGATourTournaments();
      setTournaments(tournamentsData);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching PGA Tour tournaments:', err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedTournaments.size === tournaments.length) {
      setSelectedTournaments(new Set());
    } else {
      setSelectedTournaments(new Set(tournaments.map(t => t.id)));
    }
  };

  const handleSelectTournament = (tournamentId) => {
    const newSelected = new Set(selectedTournaments);
    if (newSelected.has(tournamentId)) {
      newSelected.delete(tournamentId);
    } else {
      newSelected.add(tournamentId);
    }
    setSelectedTournaments(newSelected);
  };

  const handleConfirm = () => {
    const selectedTournamentData = tournaments.filter(t => selectedTournaments.has(t.id));
    onConfirmCreate(selectedTournamentData);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (!showModal) return null;

  return (
    <div className="pga-tour-modal-overlay">
      <div className="pga-tour-modal pga-tour-modal-content">
        <div className="pga-tour-modal-header">
          <h2>🏌️ PGA Tour Tournament Import</h2>
          <button
            onClick={() => setShowModal(false)}
            className="pga-tour-modal-close"
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              ❌ {error}
              <Button onClick={fetchPGATournaments} size="small">
                🔄 Retry
              </Button>
            </div>
          )}

          {isFetching ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Fetching PGA Tour tournaments...</p>
            </div>
          ) : (
            <>
              <div className="tournament-summary">
                <p>
                  Found <strong>{tournaments.length}</strong> upcoming tournaments from PGA Tour 2025 schedule.
                  Select the tournaments you want to import as draft tournaments.
                </p>
                <div className="selection-controls">
                  <Button
                    onClick={handleSelectAll}
                    size="small"
                    className="select-all-btn"
                  >
                    {selectedTournaments.size === tournaments.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <span className="selection-count">
                    {selectedTournaments.size} of {tournaments.length} selected
                  </span>
                </div>
              </div>

              <div className="tournaments-table-container">
                <table className="tournaments-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Tournament Name</th>
                      <th>Dates</th>
                      <th>Venue</th>
                      <th>Last Winner</th>
                      <th>Purse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tournaments.map((tournament) => (
                      <tr key={tournament.id} className="tournament-row">
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedTournaments.has(tournament.id)}
                            onChange={() => handleSelectTournament(tournament.id)}
                            className="tournament-checkbox"
                          />
                        </td>
                        <td className="tournament-name">
                          <strong>{tournament.name}</strong>
                          {tournament.isMajor && <span className="major-badge">🏆 Major</span>}
                          {tournament.isSignature && <span className="signature-badge">⭐ Signature</span>}
                        </td>
                        <td className="tournament-dates">
                          <div className="date-range">
                            <div>Start: {formatDate(tournament.startDate)}</div>
                            <div>End: {formatDate(tournament.endDate)}</div>
                          </div>
                        </td>
                        <td className="tournament-venue">{tournament.venue}</td>
                        <td className="tournament-winner">{tournament.lastWinner}</td>
                        <td className="tournament-purse">{tournament.purse}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="import-notes">
                <h4>📝 Import Details:</h4>
                <ul>
                  <li>All tournaments will be created with <strong>DRAFT</strong> status</li>
                  <li>Max players will be set to <strong>156</strong> for all tournaments</li>
                  <li>Location and last winner will be added to the description</li>
                  <li><strong>No players will be assigned</strong> - you'll need to add players separately</li>
                  <li>Existing draft tournaments with the same name will be updated</li>
                </ul>
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>
                  💡 <strong>Next steps:</strong> After import, go to Players page → Import from OWGR, then edit each tournament to add players.
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-actions">
          <Button
            onClick={() => setShowModal(false)}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="confirm-btn"
            disabled={selectedTournaments.size === 0 || isLoading}
          >
            {isLoading ? 'Creating...' : `Create ${selectedTournaments.size} Tournament${selectedTournaments.size !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PGATourFetchModal;
